import { useSyncExternalStore } from "react";

import type { ModuleDefinition, SectionDefinition } from "@/config/modules";

/**
 * Global search (#640): one panel in the shell that searches orders, contacts,
 * shipments and catalog products (#656) through their own list routes, and the pages and reports the
 * person can open, matched here in the browser.
 *
 * No new tables and no new endpoint: each group is the list's own search with
 * a limit of five, so the server's permission check for that list is the one
 * that applies, and a list the person may not read is not asked at all.
 */

/** Fewer characters than this match too much to be worth a request. */
export const MIN_SEARCH_CHARS = 2;
/** The pause before searching, as the list search waits. */
export const SEARCH_DEBOUNCE_MS = 250;
/** Rows per group; "See all N" opens the list for the rest. */
export const GROUP_LIMIT = 5;

/** The label each row carries. */
export type ResultKind =
  "Order" | "Contact" | "Shipment" | "Product" | "Doc" | "Report" | "Page";

/** Where a row goes: a route and its search params. */
export interface SearchTarget {
  to: string;
  search?: Record<string, string>;
}

export interface PageResult {
  kind: "Report" | "Page";
  label: string;
  /** The module, for a section; nothing for a module's only page. */
  detail: string | null;
  target: SearchTarget;
}

/** The term as it is searched, or null while it is too short. */
export function searchTerm(input: string): string | null {
  const term = input.trim();
  return term.length >= MIN_SEARCH_CHARS ? term : null;
}

/** "See all 42 orders", or "See all orders" when the count is unknown. */
export function seeAllLabel(total: number | null, noun: string): string {
  return total === null
    ? `See all ${noun}`
    : `See all ${total.toLocaleString("en-IN")} ${noun}`;
}

function sectionPath(
  module: ModuleDefinition,
  section: SectionDefinition,
): string {
  if (module.path === "/") return `/${section.path.replace(/^\//, "")}`;
  return `${module.path}${section.path}`;
}

/**
 * The pages and reports whose name contains the term, from the navigation the
 * person can see (`visibleModules`), so a page they may not open is never
 * offered. A section of Reports is a Report; anything else is a Page.
 */
export function matchPages(
  modules: readonly {
    module: ModuleDefinition;
    sections: readonly SectionDefinition[];
  }[],
  term: string,
): PageResult[] {
  const needle = term.toLowerCase();
  const found: PageResult[] = [];
  for (const { module, sections } of modules) {
    for (const section of sections) {
      const name = `${module.label} ${section.label}`.toLowerCase();
      if (!name.includes(needle)) continue;
      const solo = sections.length === 1;
      found.push({
        kind: module.id === "reports" ? "Report" : "Page",
        label: section.label,
        detail: solo ? null : module.label,
        target: { to: sectionPath(module, section) },
      });
    }
  }
  return found;
}

/* ---------------------------------------------------------- recent searches */

const RECENT_KEY = "estelle.recentSearches";
const RECENT_LIMIT = 5;

/**
 * What this browser searched for lately, newest first. A per-viewer
 * convenience in `localStorage`: empty in a private window or when storage is
 * blocked, and the panel works the same without it.
 */
export function readRecentSearches(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed
          .filter((item): item is string => typeof item === "string")
          .slice(0, RECENT_LIMIT)
      : [];
  } catch {
    return [];
  }
}

/** The list with `term` moved to the front, once, case-insensitively. */
export function withRecent(recent: readonly string[], term: string): string[] {
  const lower = term.toLowerCase();
  return [term, ...recent.filter((item) => item.toLowerCase() !== lower)].slice(
    0,
    RECENT_LIMIT,
  );
}

export function rememberSearch(term: string): void {
  try {
    window.localStorage.setItem(
      RECENT_KEY,
      JSON.stringify(withRecent(readRecentSearches(), term)),
    );
  } catch {
    // Storage blocked: the panel simply has no recent searches.
  }
}

/* --------------------------------------------------------------- open state */

let open = false;
const listeners = new Set<() => void>();

function setOpen(next: boolean) {
  if (open === next) return;
  open = next;
  for (const listener of listeners) listener();
}

/** Opens the panel: the header's magnifier, Ctrl+K, or "/". */
export function openGlobalSearch(): void {
  setOpen(true);
}

export function closeGlobalSearch(): void {
  setOpen(false);
}

export function useGlobalSearchOpen(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => open,
  );
}

/**
 * Whether a key press opens the panel: Ctrl+K or ⌘K anywhere, or "/" when
 * the person is not typing into a field.
 */
export function opensSearch(
  event: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey">,
  typing: boolean,
): boolean {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    return true;
  }
  return (
    event.key === "/" &&
    !typing &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.altKey
  );
}
